/* ==============================================================================
   Capacidades da plataforma
   ==============================================================================
   Um objeto de flags detectadas, e não `if (isElectron)` espalhado pelo código.

   Por que isso importa mais do que parece
   =======================================
   `isElectron` espalhado tem dois defeitos. O primeiro é conhecido: a checagem
   se repete e uma delas fica esquecida. O segundo é pior — ela responde à
   pergunta ERRADA. O que a interface precisa saber não é "estou no Electron", é
   "posso escolher o dispositivo de saída". Essas duas perguntas divergem: o
   Chromium responde sim para a segunda sem ser Electron, e uma versão futura do
   Firefox pode passar a responder sim também.

   Detecção de CAPACIDADE, não de ambiente. Quando a capacidade aparece num
   navegador, o código já funciona sem alteração.

   A regra de uso: desabilitar COM EXPLICAÇÃO
   =========================================
   A interface lê estas flags e desabilita o controle mostrando o motivo, em vez
   de esconder ou de quebrar. "Este navegador não permite escolher o fone" é uma
   informação; um seletor que não faz nada é um bug percebido.
   ============================================================================== */

export type Platform = 'web' | 'electron' | 'unknown';

export interface PlatformCapabilities {
  platform: Platform;
  /** Sistema operacional adivinhado, para rótulo de sessão. */
  os: 'windows' | 'macos' | 'linux' | 'android' | 'ios' | 'unknown';

  /** `setSinkId`: escolher o dispositivo de SAÍDA. */
  canSelectOutputDevice: boolean;
  /** `selectAudioOutput()`: caminho do Firefox, com prompt explícito. */
  canPromptOutputDevice: boolean;
  /** `getDisplayMedia`: compartilhar tela. */
  canShareScreen: boolean;
  /**
   * Captura confiável do áudio do sistema.
   *
   * `false` no navegador mesmo quando `getDisplayMedia` aceita `audio: true`:
   * ali o suporte depende do tipo de fonte escolhida e do sistema operacional,
   * então prometer é pior que não prometer.
   */
  canCaptureSystemAudio: boolean;
  /** Atalho global, que funciona com a janela em segundo plano. */
  canRegisterGlobalHotkey: boolean;
  /** AudioWorklet, base do gate e do medidor. */
  hasAudioWorklet: boolean;
  /** WebRTC com `RTCRtpSender.setParameters`, base do governador de banda. */
  canControlBitrate: boolean;
  /** Armazenamento seguro para chaves de E2EE. */
  secureKeyStore: 'safeStorage' | 'indexeddb' | 'none';
  /** `true` em contexto seguro. `getUserMedia` exige. */
  isSecureContext: boolean;
}

/**
 * Detecta as capacidades do ambiente atual.
 *
 * Chamada uma vez e memoizada: `enumerateDevices` e verificação de protótipo não
 * são caras, mas o objeto é lido em toda tela de configuração, e um valor estável
 * evita que a interface pisque entre estados.
 */
let cached: PlatformCapabilities | null = null;

export function detectCapabilities(): PlatformCapabilities {
  if (cached !== null) return cached;

  /*
   * Electron é detectado por `process.versions.electron`, não por user agent.
   *
   * User agent do Electron é configurável e frequentemente alterado para
   * parecer Chrome — justamente para não ser bloqueado por sites. Confiar nele
   * daria falso negativo. `process` só existe no preload ou com
   * `nodeIntegration`, e a ausência é um negativo confiável.
   */
  const electronVersion = (
    globalThis as unknown as { process?: { versions?: { electron?: string } } }
  ).process?.versions?.electron;

  const platform: Platform =
    electronVersion !== undefined
      ? 'electron'
      : typeof globalThis.window !== 'undefined'
        ? 'web'
        : 'unknown';

  const userAgent =
    typeof navigator !== 'undefined' ? navigator.userAgent.toLowerCase() : '';

  /*
   * `os` sai do user agent, e aqui isso é aceitável.
   *
   * A diferença: o valor é usado apenas como RÓTULO na lista de sessões
   * ("Windows · Chrome"). Nenhuma decisão de comportamento depende dele. Se o
   * user agent mentir, o rótulo fica errado e nada quebra — ao contrário do que
   * aconteceria se uma capacidade fosse inferida daqui.
   */
  const os: PlatformCapabilities['os'] = userAgent.includes('windows')
    ? 'windows'
    : userAgent.includes('mac os') || userAgent.includes('macintosh')
      ? 'macos'
      : userAgent.includes('android')
        ? 'android'
        : userAgent.includes('iphone') || userAgent.includes('ipad')
          ? 'ios'
          : userAgent.includes('linux')
            ? 'linux'
            : 'unknown';

  const hasMediaElement = typeof HTMLMediaElement !== 'undefined';

  cached = {
    platform,
    os,

    canSelectOutputDevice:
      hasMediaElement && 'setSinkId' in HTMLMediaElement.prototype,

    canPromptOutputDevice:
      typeof navigator !== 'undefined' &&
      'selectAudioOutput' in (navigator.mediaDevices ?? {}),

    canShareScreen:
      typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getDisplayMedia === 'function',

    /*
     * `true` só no Electron.
     *
     * O navegador ACEITA `audio: true` em `getDisplayMedia`, e às vezes entrega
     * — depende de o usuário escolher uma aba, do sistema operacional, e no
     * macOS de uma permissão separada. Declarar a capacidade como presente
     * levaria a interface a prometer o que falha na maioria dos casos.
     *
     * No Electron o caminho é `setDisplayMediaRequestHandler` com
     * `audio: 'loopback'`, que é determinístico. E ainda assim é o item de
     * maior risco da lista da arquitetura: o comportamento mudou entre versões
     * do Electron, e a versão fica fixada por isso.
     */
    canCaptureSystemAudio: platform === 'electron',

    canRegisterGlobalHotkey: platform === 'electron',

    hasAudioWorklet:
      typeof AudioWorkletNode !== 'undefined' &&
      typeof AudioContext !== 'undefined' &&
      'audioWorklet' in AudioContext.prototype,

    canControlBitrate:
      typeof RTCRtpSender !== 'undefined' &&
      'setParameters' in RTCRtpSender.prototype,

    secureKeyStore:
      platform === 'electron'
        ? 'safeStorage'
        : typeof indexedDB !== 'undefined'
          ? 'indexeddb'
          : 'none',

    /*
     * `isSecureContext` é a checagem que evita o diagnóstico mais desperdiçado
     * de todos.
     *
     * `getUserMedia` e `getDisplayMedia` são `undefined` em origem insegura —
     * não lançam, não pedem permissão, simplesmente não existem. Quem abre o
     * app por `http://192.168.0.10:5173` vê "seu navegador não suporta
     * microfone" quando o problema é o `http`. `localhost` é exceção e conta
     * como seguro.
     */
    isSecureContext:
      typeof globalThis.isSecureContext === 'boolean'
        ? globalThis.isSecureContext
        : false,
  };

  return cached;
}

/**
 * Explica por que uma capacidade falta, em linguagem de usuário.
 *
 * Existe para a interface não precisar montar essas frases em cada tela — e para
 * a explicação ser a MESMA em todas, o que importa quando a pessoa encontra a
 * limitação em dois lugares diferentes e precisa entender que é o mesmo motivo.
 */
export function explainMissing(
  capability: keyof PlatformCapabilities,
  capabilities: PlatformCapabilities = detectCapabilities(),
): string | null {
  switch (capability) {
    case 'canSelectOutputDevice':
      if (capabilities.canSelectOutputDevice) return null;
      return capabilities.canPromptOutputDevice
        ? 'Este navegador exige escolher o fone por um diálogo do sistema. Use o ' +
            'botão "Escolher saída".'
        : 'Este navegador não permite escolher o dispositivo de saída. O áudio vai ' +
            'para o padrão do sistema. O aplicativo de desktop permite escolher.';

    case 'canShareScreen':
      if (capabilities.canShareScreen) return null;
      return capabilities.isSecureContext
        ? 'Este navegador não suporta compartilhamento de tela.'
        : 'Compartilhamento de tela exige HTTPS. Abra o Concord pelo endereço ' +
            'seguro.';

    case 'canCaptureSystemAudio':
      if (capabilities.canCaptureSystemAudio) return null;
      return 'A captura do áudio do sistema só é confiável no aplicativo de ' +
        'desktop. No navegador, o áudio da tela compartilhada pode não ser ' +
        'incluído.';

    case 'canRegisterGlobalHotkey':
      if (capabilities.canRegisterGlobalHotkey) return null;
      return 'No navegador, o atalho de falar só funciona com a aba em foco. O ' +
        'aplicativo de desktop registra um atalho global.';

    case 'hasAudioWorklet':
      if (capabilities.hasAudioWorklet) return null;
      return 'Este navegador não suporta AudioWorklet: o filtro de ruído e o ' +
        'medidor de nível ficam indisponíveis. A voz continua funcionando.';

    case 'isSecureContext':
      if (capabilities.isSecureContext) return null;
      return 'Microfone e câmera exigem HTTPS. Em rede local, use localhost ou ' +
        'configure um certificado.';

    default:
      return null;
  }
}
